import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { BarChart3, LogOut, RefreshCw, Inbox, Mail, Download } from "lucide-react";
import { Wordmark } from "@/components/site/Header";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "weyb-admin-token";

function formatDate(iso) {
    try {
        return new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
    } catch {
        return iso;
    }
}

function StatCard({ testid, label, value, icon: Icon }) {
    return (
        <div className="bg-white border border-gray-200 rounded-sm p-6" data-testid={testid}>
            <div className="flex items-center gap-2 text-gray-400">
                <Icon size={16} strokeWidth={1.5} aria-hidden="true" />
                <span className="text-[11px] uppercase tracking-[0.18em] font-semibold">{label}</span>
            </div>
            <p className="mt-3 font-display text-4xl font-light text-weyb-ink" data-testid={`${testid}-value`}>{value}</p>
        </div>
    );
}

export default function Admin() {
    useEffect(() => {
        document.title = "Espace admin — WEYB";
        let m = document.querySelector('meta[name="robots"]');
        if (!m) { m = document.createElement("meta"); m.name = "robots"; document.head.appendChild(m); }
        m.setAttribute("content", "noindex, nofollow");
    }, []);
    const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [stats, setStats] = useState(null);

    const exportCsv = async (collection) => {
        try {
            const res = await axios.get(`${API}/admin/export`, {
                params: { collection, scope: "month" },
                headers: { Authorization: `Bearer ${token}` },
                responseType: "blob",
            });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `weyb-${collection}-mois.csv`;
            a.click();
            URL.revokeObjectURL(url);
        } catch {
            setError("Export impossible pour le moment.");
        }
    };

    const logout = useCallback(() => {
        sessionStorage.removeItem(TOKEN_KEY);
        setToken("");
        setStats(null);
    }, []);

    const loadStats = useCallback(async (tok) => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${tok}` } });
            setStats(res.data);
            setError("");
        } catch (e) {
            if (e.response?.status === 401) {
                logout();
                setError("Session expirée — reconnectez-vous.");
            } else {
                setError("Impossible de charger les statistiques.");
            }
        } finally {
            setLoading(false);
        }
    }, [logout]);

    useEffect(() => {
        if (token) loadStats(token);
    }, [token, loadStats]);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError("");
        try {
            const res = await axios.post(`${API}/admin/login`, { password });
            sessionStorage.setItem(TOKEN_KEY, res.data.token);
            setToken(res.data.token);
        } catch (err) {
            const status = err.response?.status;
            setError(status === 429 ? "Trop de tentatives — réessayez dans quelques minutes." : "Mot de passe incorrect.");
        } finally {
            setLoading(false);
        }
    };

    if (!token) {
        return (
            <div className="min-h-screen bg-weyb-ink text-white flex items-center justify-center px-6">
                <form onSubmit={submit} data-testid="admin-login-form" className="w-full max-w-sm">
                    <div className="mb-10"><Wordmark dark /></div>
                    <h1 className="font-display text-3xl font-light tracking-tight">Espace WEYB</h1>
                    <p className="mt-2 text-sm text-white/50">Accès réservé — statistiques des demandes du site.</p>
                    <label htmlFor="admin-password" className="mt-8 block text-[11px] uppercase tracking-[0.18em] font-semibold text-weyb-yellow">Mot de passe</label>
                    <input
                        id="admin-password"
                        data-testid="admin-password-input"
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        autoComplete="current-password"
                        className="mt-2 w-full bg-white/5 border border-white/20 rounded-sm px-4 py-3 text-sm text-white placeholder-white/30 focus:border-weyb-yellow focus:outline-none transition-colors"
                        placeholder="••••••••••••"
                    />
                    {error && <p data-testid="admin-error" className="mt-3 text-sm text-red-400">{error}</p>}
                    <button
                        type="submit"
                        data-testid="admin-login-submit"
                        disabled={loading || !password}
                        className="mt-6 w-full bg-weyb-yellow text-weyb-ink hover:bg-weyb-yellow-dark disabled:opacity-50 px-6 py-3.5 rounded-sm text-sm font-semibold transition-colors"
                    >
                        {loading ? "Connexion…" : "Accéder au tableau de bord"}
                    </button>
                </form>
            </div>
        );
    }

    const monthLabel = new Date().toLocaleDateString("fr-FR", { month: "long", year: "numeric" });

    return (
        <div className="min-h-screen bg-[#F9FAFB]">
            <header className="bg-weyb-ink text-white">
                <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Wordmark dark />
                        <span className="text-[11px] uppercase tracking-[0.18em] font-semibold text-weyb-yellow">Espace admin</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => loadStats(token)}
                            data-testid="admin-refresh-button"
                            className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-weyb-yellow border border-white/20 hover:border-weyb-yellow px-4 py-2 rounded-sm transition-colors"
                        >
                            <RefreshCw size={14} aria-hidden="true" /> Actualiser
                        </button>
                        <button
                            onClick={() => exportCsv("sourcing")}
                            data-testid="admin-export-sourcing"
                            className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-weyb-yellow border border-white/20 hover:border-weyb-yellow px-4 py-2 rounded-sm transition-colors"
                        >
                            <Download size={14} aria-hidden="true" /> Sourcing CSV
                        </button>
                        <button
                            onClick={() => exportCsv("contact")}
                            data-testid="admin-export-contact"
                            className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-weyb-yellow border border-white/20 hover:border-weyb-yellow px-4 py-2 rounded-sm transition-colors"
                        >
                            <Download size={14} aria-hidden="true" /> Contact CSV
                        </button>
                        <button
                            onClick={logout}
                            data-testid="admin-logout-button"
                            className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-weyb-yellow border border-white/20 hover:border-weyb-yellow px-4 py-2 rounded-sm transition-colors"
                        >
                            <LogOut size={14} aria-hidden="true" /> Quitter
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-6 py-10" data-testid="admin-dashboard">
                <div className="flex items-end justify-between flex-wrap gap-3">
                    <div>
                        <h1 className="font-display text-3xl md:text-4xl font-light tracking-tight text-weyb-ink">Demandes reçues</h1>
                        <p className="mt-1 text-sm text-gray-500">Formulaires Sourcing MENA et Contact — mois de {monthLabel} et cumul.</p>
                    </div>
                    {loading && <span className="text-xs text-gray-400">Chargement…</span>}
                </div>

                {error && <p data-testid="admin-error" className="mt-4 text-sm text-red-500">{error}</p>}

                {stats && (
                    <>
                        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                            <StatCard testid="stat-sourcing-month" label={`Sourcing — ${monthLabel}`} value={stats.sourcing.month} icon={BarChart3} />
                            <StatCard testid="stat-sourcing-total" label="Sourcing — total" value={stats.sourcing.total} icon={Inbox} />
                            <StatCard testid="stat-contact-month" label={`Contact — ${monthLabel}`} value={stats.contacts.month} icon={BarChart3} />
                            <StatCard testid="stat-contact-total" label="Contact — total" value={stats.contacts.total} icon={Mail} />
                        </div>

                        <h2 className="mt-12 font-display text-2xl font-light text-weyb-ink">Dernières demandes</h2>
                        <div className="mt-4 bg-white border border-gray-200 rounded-sm overflow-x-auto">
                            <table className="w-full text-sm" data-testid="admin-leads-table">
                                <thead>
                                    <tr className="text-left text-[11px] uppercase tracking-[0.14em] text-gray-400 border-b border-gray-100">
                                        <th className="px-5 py-3 font-semibold">Type</th>
                                        <th className="px-5 py-3 font-semibold">Nom</th>
                                        <th className="px-5 py-3 font-semibold">Société</th>
                                        <th className="px-5 py-3 font-semibold">Email</th>
                                        <th className="px-5 py-3 font-semibold">Détail</th>
                                        <th className="px-5 py-3 font-semibold">Date</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {stats.recent.length === 0 && (
                                        <tr><td colSpan="6" className="px-5 py-8 text-center text-gray-400">Aucune demande pour le moment.</td></tr>
                                    )}
                                    {stats.recent.map((lead, i) => (
                                        <tr key={i} data-testid={`admin-lead-row-${i}`} className="border-b border-gray-50 last:border-0">
                                            <td className="px-5 py-3">
                                                <span className={`inline-block px-2.5 py-1 rounded-sm text-[11px] font-semibold ${lead.type === "sourcing" ? "bg-weyb-yellow/20 text-weyb-ink" : "bg-gray-100 text-gray-600"}`}>
                                                    {lead.type === "sourcing" ? "Sourcing" : "Contact"}
                                                </span>
                                            </td>
                                            <td className="px-5 py-3 text-weyb-ink font-medium">{lead.name}</td>
                                            <td className="px-5 py-3 text-gray-600">{lead.company || "—"}</td>
                                            <td className="px-5 py-3 text-gray-600">{lead.email}</td>
                                            <td className="px-5 py-3 text-gray-500">{lead.detail || "—"}</td>
                                            <td className="px-5 py-3 text-gray-400 whitespace-nowrap">{formatDate(lead.created_at)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </main>
        </div>
    );
}
